import { type ReactNode } from "react";
import { paletteFor, hexToRgba } from "../lib/palette";
import { useResolvedTheme, type Theme } from "../lib/theme";
import { cn } from "../lib/cn";

/** One sector-ETF tile (single-value; the heatmap has no sparkline). */
export interface SectorHeatmapTile {
  /** ETF ticker, e.g. `XLK`. */
  symbol: string;
  /** Sector display name, e.g. `Technology`. */
  name: string;
  /** Signed daily change, in percent (e.g. `1.24` = +1.24%). */
  changePercent: number;
}

export interface SectorHeatmapProps {
  sectors: SectorHeatmapTile[];
  theme?: Theme;
  /** Empty-state copy when no sectors are available. */
  emptyLabel?: ReactNode;
  /**
   * When set, each tile becomes a button that reports its ETF `symbol` — the
   * market page uses it to drill into that sector's per-stock treemap. Omit for
   * the plain, non-interactive heatmap.
   */
  onSelect?: (symbol: string) => void;
  className?: string;
}

/** |change| that saturates the tile tint. */
const CLAMP_PCT = 3;
/** Max background opacity at full saturation — kept moderate so text stays legible. */
const MAX_ALPHA = 0.62;

/** Signed percent text; the printed sign is the second (non-color) channel. */
function signedPct(pct: number): string {
  const v = pct.toFixed(2);
  return pct > 0 ? `+${v}%` : `${v}%`;
}

/**
 * Sector heatmap: a responsive grid of the 11 SPDR sector ETFs, sorted by daily
 * change. Each tile's background intensity scales with |changePercent| (green for
 * gainers, red for losers), interpolated from the card surface toward good/critical
 * and clamped at ±3%. The signed percent is always printed, so direction never
 * relies on color alone, and text stays on the theme ink for contrast in both modes.
 */
export function SectorHeatmap({
  sectors,
  theme,
  emptyLabel = "No sector data",
  onSelect,
  className,
}: SectorHeatmapProps): ReactNode {
  const resolved = useResolvedTheme(theme);
  const palette = paletteFor(resolved);

  if (sectors.length === 0) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-lg border border-dashed border-hairline py-12 text-sm text-muted",
          className,
        )}
      >
        {emptyLabel}
      </div>
    );
  }

  const sorted = [...sectors].sort((a, b) => b.changePercent - a.changePercent);

  return (
    <div className={cn("grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-4", className)}>
      {sorted.map((s) => {
        const intensity = Math.min(Math.abs(s.changePercent), CLAMP_PCT) / CLAMP_PCT;
        const base = s.changePercent >= 0 ? palette.good : palette.critical;
        const bg = hexToRgba(base, intensity * MAX_ALPHA);
        const inner = (
          <>
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-ink" title={s.name}>
                {s.name}
              </div>
              {/* ink-secondary, not muted: must stay legible on full-tint tiles. */}
              <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-secondary">
                {s.symbol}
              </div>
            </div>
            <div className="text-base font-semibold tabular-nums text-ink">
              {signedPct(s.changePercent)}
            </div>
          </>
        );
        if (onSelect) {
          return (
            <button
              key={s.symbol}
              type="button"
              onClick={() => onSelect(s.symbol)}
              className="flex flex-col justify-between gap-2 rounded-lg border border-hairline p-3 text-left transition-shadow hover:shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
              style={{ backgroundColor: bg }}
            >
              {inner}
            </button>
          );
        }
        return (
          <div
            key={s.symbol}
            className="flex flex-col justify-between gap-2 rounded-lg border border-hairline p-3"
            style={{ backgroundColor: bg }}
          >
            {inner}
          </div>
        );
      })}
    </div>
  );
}
